export type Locale = 'en' | 'pt-BR';

/**
 * Each language is its own site and its own build: English on gizmodata.com, Brazilian Portuguese on
 * gizmodata.com.br, with the same paths on both (gizmodata.com/cloud ↔ gizmodata.com.br/cloud).
 * `npm run build` builds both (scripts/build-sites.mjs sets VITE_SITE_LOCALE for each); nginx picks the
 * build by Host. Outside Vite (node tests) there is no import.meta.env, and the site is English.
 */
export const SITE_ORIGINS: Readonly<Record<Locale, string>> = { en: 'https://gizmodata.com', 'pt-BR': 'https://gizmodata.com.br' };
// Written out literally: Vite only replaces `import.meta.env` where it appears verbatim.
export const SITE_LOCALE: Locale = import.meta.env?.VITE_SITE_LOCALE === 'pt-BR' ? 'pt-BR' : 'en';
export const SITE_URL = SITE_ORIGINS[SITE_LOCALE];
export const LANGUAGE_PREFERENCE_KEY = 'gizmodata.language.v1';
export const LANGUAGE_DISMISSED_KEY = 'gizmodata.language-suggestion.dismissed.v1';

/** Route identity without the retired `/pt-br` prefix (old links and copy still carry it; nginx redirects them). */
export function englishPath(path: string): string {
  const result = path.replace(/^\/pt-br(?=\/|$)/i, '') || '/';
  return result.length > 1 ? result.replace(/\/+$/, '') : result;
}

const siteOf = (origin: string): Locale | undefined =>
  (Object.keys(SITE_ORIGINS) as Locale[]).find(locale => SITE_ORIGINS[locale] === origin.replace('://www.', '://'));

/**
 * A website link in `locale`. Same-site links stay as written (relative stays relative); a link into the
 * other language's site becomes absolute. Files, other origins and protocols stay intact. `site` is the
 * language of the page the link is on (tests pass it; pages use the build's).
 */
export function localizePath(value: string, locale: Locale, site: Locale = SITE_LOCALE): string {
  if (!value || value.startsWith('#') || value.startsWith('?')) return value;
  const absolute = /^https?:\/\//i.test(value);
  if (!absolute && !value.startsWith('/')) return value;
  if (value.startsWith('//')) return value;
  let parsed: URL;
  try { parsed = new URL(value, SITE_ORIGINS[site]); } catch { return value; }
  if (!siteOf(parsed.origin)) return value;
  const path = englishPath(parsed.pathname);
  if ((/\.[a-z0-9]+$/i.test(path) && path !== '/gizmosql-pitch-deck.html') || /^\/(assets|logos|lovable-uploads|nexus|team|events|pt-br-assets)\//.test(path)) return value;
  const origin = absolute || locale !== site ? SITE_ORIGINS[locale] : '';
  return `${origin}${path}${parsed.search}${parsed.hash}`;
}

/**
 * The other language's site as seen from this one, at runtime: production pairs with production, and the
 * dev preview and local servers pair with their own counterparts (dev.gizmodata.com ↔ dev.gizmodata.com.br,
 * localhost ↔ pt.localhost), so a language switch there doesn't jump to the live site.
 */
export function counterpartOrigin(location: { protocol: string; hostname: string; port: string }, locale: Locale): string {
  const { protocol, hostname, port } = location;
  const suffix = port ? `:${port}` : '';
  if (hostname === 'dev.gizmodata.com' || hostname === 'dev.gizmodata.com.br') return `https://dev.${new URL(SITE_ORIGINS[locale]).hostname}`;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'pt.localhost') return `${protocol}//${locale === 'pt-BR' ? 'pt.localhost' : '127.0.0.1'}${suffix}`;
  return SITE_ORIGINS[locale];
}

/**
 * Whether this page load is a language switch: the visitor came straight from the same site in the other
 * language (the switch is a link to the other domain, so it shows up as the referrer). Browser-only.
 */
export function arrivedFromOtherLanguageSite(): boolean {
  const loopback = (origin: string) => origin.replace('//localhost', '//127.0.0.1');
  const other: Locale = SITE_LOCALE === 'en' ? 'pt-BR' : 'en';
  try {
    return !!document.referrer && loopback(new URL(document.referrer).origin) === loopback(counterpartOrigin(window.location, other));
  } catch { return false; }
}

export function preferredLanguage(languages: readonly string[]): Locale | undefined {
  for (const language of languages) {
    if (/^pt(?:-|$)/i.test(language)) return 'pt-BR';
    if (/^en(?:-|$)/i.test(language)) return 'en';
  }
  return undefined;
}

export function readPreference(key: string): string | null {
  try { return typeof window === 'undefined' ? null : window.localStorage.getItem(key); } catch { return null; }
}

export function savePreference(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* Navigation works without storage. */ }
}
